import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import AppLink from '@/components/AppLink/AppLink';
import AtrivialLogo from '@/components/IconLogo/AtrivialLogo';
import {
  NewsletterMarketingNavCTAs,
  NewsletterMarketingNavLinks,
  NewsletterMarketingNavMenu,
} from './NewsletterMarketingNav';

function observeMarketingElement(
  id: string,
  onVisibilityChange: (visible: boolean) => void,
  options: IntersectionObserverInit
) {
  const observer = new IntersectionObserver(([entry]) => {
    onVisibilityChange(entry.isIntersecting);
  }, options);
  const attach = () => {
    const element = document.getElementById(id);
    if (!element) return false;
    observer.observe(element);
    return true;
  };
  // Route content can arrive after the common layout has mounted.
  let mutationObserver: MutationObserver | undefined;
  if (!attach()) {
    mutationObserver = new MutationObserver(() => {
      if (attach()) mutationObserver?.disconnect();
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });
  }
  return () => {
    mutationObserver?.disconnect();
    observer.disconnect();
  };
}

/** Publisher navigation has no dependency on player settings, XP, or the game engine. */
export default function MarketingLayout({ children }: { children: ReactNode }) {
  const { pathname, hash } = useLocation();
  const headerRef = useRef<HTMLElement>(null);
  const [pinned, setPinned] = useState(false);
  const [heroTrialVisible, setHeroTrialVisible] = useState(true);

  useEffect(() => {
    // A link from another page, such as /#pricing from an article, can arrive before the
    // section renders, which leaves the page at the top. Finish the jump once it exists.
    if (!hash || window.scrollY > 0) return;
    document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [pathname, hash]);

  useEffect(() => {
    setPinned(false);
    return observeMarketingElement('marketing-nav-sentinel', (visible) => setPinned(!visible), {
      threshold: 0,
    });
  }, [pathname]);

  useEffect(() => {
    setHeroTrialVisible(true);
    if (pathname !== '/') return;
    const headerHeight = headerRef.current?.getBoundingClientRect().height ?? 0;
    return observeMarketingElement('newsletter-hero-trial', setHeroTrialVisible, {
      threshold: 0,
      // Content covered by the sticky header is no longer visible.
      rootMargin: `-${headerHeight}px 0px 0px 0px`,
    });
  }, [pathname]);

  return (
    <div className="nl-marketing-layout relative flex min-h-screen-safe w-full flex-col">
      <header
        ref={headerRef}
        className={`pointer-events-none sticky top-0 right-0 left-0 z-header box-border grid h-16 min-h-16 w-full max-w-full grid-cols-marketing-nav items-center border-b px-4 md:px-6 lg:grid-cols-marketing-nav-desktop ${pinned ? 'border-nl-line bg-nl-paper/90 backdrop-blur-marketing backdrop-saturate-marketing' : 'nl-marketing-header-surface border-transparent'}`}
      >
        <AppLink
          to="/"
          className="header-logo pointer-events-auto flex shrink-0 items-center justify-self-start"
          onClick={() => {
            if (pathname === '/') window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        >
          <AtrivialLogo height={20} />
        </AppLink>
        <div className="pointer-events-auto hidden min-w-0 justify-self-center lg:flex">
          <NewsletterMarketingNavLinks />
        </div>
        <nav className="header-nav pointer-events-auto flex min-w-0 shrink-0 items-center gap-2 justify-self-end overflow-visible sm:gap-3">
          <div className="flex shrink-0 items-center">
            <NewsletterMarketingNavCTAs showTrialCta={pathname !== '/' || !heroTrialVisible} />
          </div>
          <NewsletterMarketingNavMenu />
        </nav>
      </header>
      <main className="flex w-full flex-1 flex-col">{children}</main>
    </div>
  );
}
